<template>
  <div class="permission-matrix">
    <div class="module-tabs mb-4">
      <b-nav tabs>
        <b-nav-item
          v-for="module in getAvailableModules"
          :key="module.value"
          :active="activeModule === module.value"
          @click="handleModuleChange(module.value)"
        >
          {{ module.label }}
        </b-nav-item>
      </b-nav>
    </div>

    <div class="global-controls mb-4">
      <table class="global-controls-table">
        <tbody>
          <tr>
            <td class="global-group-cell">
              <h6 class="mb-0">Quyền toàn module:</h6>
            </td>
            <td class="global-function-cell"></td>
            <td
              v-for="permissionType in permissionTypes"
              :key="permissionType.value"
              class="global-permission-cell text-center"
            >
              <b-form-checkbox
                v-if="hasAnyMappingForPermissionType(permissionType.value)"
                :checked="isModulePermissionChecked(permissionType.value)"
                :indeterminate="
                  isModulePermissionIndeterminate(permissionType.value)
                "
                @change="
                  handleModulePermissionChange(permissionType.value, $event)
                "
                class="permission-checkbox"
              >
                <span> {{ permissionType.label }}</span>
              </b-form-checkbox>
              <span v-else class="no-mapping-indicator">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="permission-table">
      <table class="permission-table-content">
        <thead>
          <tr>
            <th class="group-header text-center">Nhóm</th>
            <th class="function-headertext-center">Chức năng</th>
            <th
              v-for="permissionType in permissionTypes"
              :key="permissionType.value"
              class="permission-header text-center"
            >
              {{ permissionType.label }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in tableItems" :key="item.id" class="function-row">
            <td
              v-if="item.isFirstInGroup"
              :rowspan="item.groupRowspan"
              class="group-cell align-middle fw-bold"
            >
              {{ item.group }}
            </td>

            <td class="function-cell">
              <div class="function-name">
                {{ item.name }}
              </div>
            </td>

            <td
              v-for="permissionType in permissionTypes"
              :key="permissionType.value"
              class="permission-cell text-center"
            >
              <b-form-checkbox
                v-if="hasMappingForPermission(item.id, permissionType.value)"
                :checked="getPermissionValue(item.id, permissionType.value)"
                :indeterminate="isPermissionIndeterminate()"
                @change="
                  handlePermissionChange(item.id, permissionType.value, $event)
                "
                class="permission-checkbox"
              />
              <span v-else class="no-mapping-indicator">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <b-overlay :show="loading" rounded="sm" opacity="0.6">
      <div v-if="loading" class="loading-placeholder"></div>
    </b-overlay>

    <div v-if="allErrors.length > 0" class="alert alert-danger mt-3">
      <h6>Lỗi validation:</h6>
      <ul class="mb-0">
        <li v-for="error in allErrors" :key="error">{{ error }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { permissionMatrixMixin } from '@/view/mixins/permissionMatrix.mixin';
import {
  PERMISSION_TYPES,
  PERMISSION_LABELS,
  PERMISSION_MATRIX,
} from '@/constants/permissionMatrix';

export default {
  name: 'PermissionMatrix',
  mixins: [permissionMatrixMixin],
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    initialPermissions: {
      type: Array,
      default: () => [],
    },
    validateOnChange: {
      type: Boolean,
      default: true,
    },
    onPermissionChange: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      localErrors: [],
      PERMISSION_TYPES,
    };
  },
  computed: {
    permissionTypes() {
      return Object.keys(PERMISSION_TYPES).map((key) => ({
        value: PERMISSION_TYPES[key],
        label: PERMISSION_LABELS[PERMISSION_TYPES[key]],
      }));
    },

    tableItems() {
      const items = [];

      this.getCurrentModuleMatrix.forEach((group) => {
        group.functions.forEach((func, index) => {
          items.push({
            id: func.id,
            name: func.name,
            isGroup: false,
            group: group.group,
            function: func.name,
            groupRowspan: index === 0 ? group.functions.length : 0,
            isFirstInGroup: index === 0,
          });
        });
      });

      return items;
    },

    allErrors() {
      return [...this.errors, ...this.localErrors];
    },
  },
  mounted() {
    this.initializeMatrix();

    if (this.initialPermissions.length > 0) {
      this.loadPermissions(this.initialPermissions);
    }
  },
  methods: {
    isModulePermissionChecked(permissionType) {
      if (permissionType === PERMISSION_TYPES.ALL) {
        const functionsWithAnyMapping = this.getCurrentModuleMatrix
          .flatMap((group) => group.functions)
          .filter((func) => this.hasAnyMappingForFunction(func.id));

        if (functionsWithAnyMapping.length === 0) {
          return false;
        }

        const result = functionsWithAnyMapping.every((func) => {
          const mappedTypes = Object.values(PERMISSION_TYPES)
            .filter((type) => type !== PERMISSION_TYPES.ALL)
            .filter((type) => this.hasMappingForPermission(func.id, type));
          const hasAllPermissions = mappedTypes.every((type) =>
            this.getPermissionValue(func.id, type),
          );
          return hasAllPermissions;
        });
        return result;
      }

      const functionsWithMapping = this.getCurrentModuleMatrix
        .flatMap((group) => group.functions)
        .filter((func) =>
          this.hasMappingForPermission(func.id, permissionType),
        );

      if (functionsWithMapping.length === 0) {
        return false;
      }

      return functionsWithMapping.every((func) =>
        this.getPermissionValue(func.id, permissionType),
      );
    },

    isModulePermissionIndeterminate(permissionType) {
      if (permissionType === PERMISSION_TYPES.ALL) {
        const functionsWithAnyMapping = this.getCurrentModuleMatrix
          .flatMap((group) => group.functions)
          .filter((func) => this.hasAnyMappingForFunction(func.id));

        if (functionsWithAnyMapping.length === 0) {
          return false;
        }

        const fullyCheckedCount = functionsWithAnyMapping.filter((func) => {
          const hasAllPermissions = Object.values(PERMISSION_TYPES)
            .filter((type) => type !== PERMISSION_TYPES.ALL)
            .filter((type) => this.hasMappingForPermission(func.id, type))
            .every((type) => this.getPermissionValue(func.id, type));
          return hasAllPermissions;
        }).length;

        return (
          fullyCheckedCount > 0 &&
          fullyCheckedCount < functionsWithAnyMapping.length
        );
      }

      const functionsWithMapping = this.getCurrentModuleMatrix
        .flatMap((group) => group.functions)
        .filter((func) =>
          this.hasMappingForPermission(func.id, permissionType),
        );

      if (functionsWithMapping.length === 0) {
        return false;
      }

      const checkedCount = functionsWithMapping.filter((func) =>
        this.getPermissionValue(func.id, permissionType),
      ).length;

      return checkedCount > 0 && checkedCount < functionsWithMapping.length;
    },

    isPermissionIndeterminate() {
      return false;
    },

    // Get row CSS class
    getRowClass(item) {
      if (item.isGroup) {
        return 'group-row';
      }
      return 'function-row';
    },

    handleModuleChange(module) {
      this.setActiveModule(module);
      this.$emit('module-change', module);
    },

    handlePermissionChange(functionId, permissionType, value) {
      this.updatePermission(functionId, permissionType, value);

      const changeData = {
        functionId,
        permissionType,
        value,
        module: this.activeModule,
        legacyPermissions: this.getLegacyPermissions,
      };

      this.$emit('permission-change', changeData);

      if (this.onPermissionChange) {
        this.onPermissionChange(changeData);
      }
    },

    handleModulePermissionChange(permissionType, value) {
      if (permissionType === PERMISSION_TYPES.ALL) {
        const functionsWithAnyMapping = this.getCurrentModuleMatrix
          .flatMap((group) => group.functions)
          .filter((func) => this.hasAnyMappingForFunction(func.id));

        functionsWithAnyMapping.forEach((func) => {
          Object.values(PERMISSION_TYPES)
            .filter((type) => type !== PERMISSION_TYPES.ALL)
            .filter((type) => this.hasMappingForPermission(func.id, type))
            .forEach((type) => {
              this.updatePermission(func.id, type, value);
            });
        });

        const changeData = {
          permissionType,
          value,
          module: this.activeModule,
          legacyPermissions: this.getLegacyPermissions,
          affectedFunctions: functionsWithAnyMapping.map((f) => f.id),
        };

        this.$emit('permission-change', changeData);

        if (this.onPermissionChange) {
          this.onPermissionChange(changeData);
        }
        return;
      }

      const functionsWithMapping = this.getCurrentModuleMatrix
        .flatMap((group) => group.functions)
        .filter((func) =>
          this.hasMappingForPermission(func.id, permissionType),
        );

      functionsWithMapping.forEach((func) => {
        this.updatePermission(func.id, permissionType, value);
      });

      const changeData = {
        permissionType,
        value,
        module: this.activeModule,
        legacyPermissions: this.getLegacyPermissions,
        affectedFunctions: functionsWithMapping.map((f) => f.id),
      };

      this.$emit('permission-change', changeData);

      if (this.onPermissionChange) {
        this.onPermissionChange(changeData);
      }
    },

    handleValidationError() {
      if (this.allErrors.length > 0) {
        this.$emit('validation-error', this.allErrors);
      }
    },

    getPermissionValue(functionId, permissionType) {
      return this.getPermission(functionId, permissionType);
    },

    hasMappingForPermission(functionId, permissionType) {
      if (permissionType === PERMISSION_TYPES.ALL) {
        return true;
      }
      const moduleMatrix = PERMISSION_MATRIX[this.activeModule];
      if (!moduleMatrix) return false;

      for (const group of moduleMatrix) {
        const functionItem = group.functions.find(
          (func) => func.id === functionId,
        );
        if (functionItem && functionItem.legacyMappings) {
          return functionItem.legacyMappings.hasOwnProperty(permissionType);
        }
      }
      return false;
    },

    hasAnyMappingForPermissionType(permissionType) {
      if (permissionType === PERMISSION_TYPES.ALL) {
        return this.hasAnyMappingInModule();
      }

      const functions = this.getCurrentModuleMatrix.flatMap(
        (group) => group.functions,
      );

      return functions.some((func) =>
        this.hasMappingForPermission(func.id, permissionType),
      );
    },

    hasAnyMappingInModule() {
      const functions = this.getCurrentModuleMatrix.flatMap(
        (group) => group.functions,
      );

      return functions.some((func) => {
        return this.hasAnyMappingForFunction(func.id);
      });
    },

    hasAnyMappingForFunction(functionId) {
      const hasAny = Object.values(PERMISSION_TYPES).some((permissionType) =>
        this.hasMappingForPermission(functionId, permissionType),
      );
      return hasAny;
    },
  },
  watch: {
    errors: {
      handler(newErrors) {
        if (newErrors.length > 0) {
          this.handleValidationError();
        }
      },
      immediate: true,
    },

    initialPermissions: {
      handler(newPermissions) {
        if (newPermissions && newPermissions.length > 0) {
          this.loadPermissions(newPermissions);
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.permission-matrix {
  .module-tabs {
    .nav-tabs {
      border-bottom: 2px solid #dee2e6;
      flex-wrap: nowrap;
      overflow-x: auto;

      .nav-item {
        margin-right: 0;
        flex-shrink: 0;
        .nav-link {
          border: none;
          border-bottom: 2px solid transparent;
          color: #6c757d;
          font-weight: 500;
          padding: 0.5rem 1.3rem;
          white-space: nowrap;
          font-size: 1rem;

          &:hover {
            border-bottom-color: #007bff;
            color: #007bff;
          }

          &.active {
            border-bottom-color: #007bff;
            color: #007bff;
            background-color: transparent;
          }
        }
      }
    }
  }

  .global-controls {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 0.375rem;
    border: 1px solid #dee2e6;

    .global-controls-table {
      width: 100%;
      min-width: 950px;
      border-collapse: collapse;
      table-layout: fixed;

      td {
        border: 1px solid transparent;
        vertical-align: middle;
      }

      .global-group-cell {
        width: 180px;
        text-align: left;
      }

      .global-function-cell {
        width: 280px;
        text-align: left;
      }

      .global-permission-cell {
        width: 70px;
        min-width: 70px;
        text-align: center;
      }
    }

    .permission-checkbox {
      margin-bottom: 0;
      font-weight: 500;
    }

    .no-mapping-indicator {
      color: #6c757d;
      font-weight: 500;
      font-size: 0.9rem;
    }
  }

  .permission-table {
    overflow-x: auto;

    .permission-table-content {
      width: 100%;
      min-width: 950px; /* Reduced width without description column */
      border-collapse: collapse;
      table-layout: fixed;

      th {
        background-color: #f8f9fa;
        font-weight: 600;
        border: 1px solid #dee2e6;
        padding: 0.75rem;
        vertical-align: middle;
        text-align: center;
      }

      .group-header {
        width: 180px;
        text-align: left;
      }

      .function-header {
        width: 220px;
        text-align: left;
      }

      .permission-header {
        width: 70px;
        min-width: 70px;
      }

      td {
        border: 1px solid #dee2e6;
        padding: 0.75rem;
        vertical-align: middle;
      }

      .function-row {
        &:hover {
          background-color: #f8f9fa;
        }
      }

      .group-cell {
        background-color: #e9ecef !important;
        font-weight: 600;
        border-top: 2px solid #dee2e6;
      }

      .group-cell,
      .function-cell {
        text-align: left;
      }

      .permission-cell {
        text-align: center;
      }

      .group-header {
        .group-header {
          font-size: 1rem;
          color: #495057;
        }
      }

      .function-info {
        .function-name {
          font-weight: 500;
          color: #212529;
          margin-bottom: 0.25rem;
        }

        .function-description {
          font-size: 0.875rem;
          color: #6c757d;
        }
      }

      .description-text {
        font-size: 0.875rem;
        color: #6c757d;
        line-height: 1.4;
        word-wrap: break-word;
        text-align: left;
      }

      .permission-cell {
        text-align: center;

        .permission-checkbox {
          margin: 0;
        }
      }
    }
  }

  .loading-placeholder {
    height: 200px;
    background-color: #f8f9fa;
    border-radius: 0.375rem;
  }
}
</style>
